package songwritersnotebook.app.components

import songwritersnotebook.app.screens.components.CreditCountdownTimer

import androidx.compose.animation.core.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.Orientation
import androidx.compose.foundation.gestures.draggable
import androidx.compose.foundation.gestures.rememberDraggableState
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import songwritersnotebook.app.LocalPlatformContext
import songwritersnotebook.app.adaptiveSheetWidth
import songwritersnotebook.app.data.UserProfile
import songwritersnotebook.app.viewmodels.EditorUiState
import songwritersnotebook.app.viewmodels.EditorViewModel
import songwritersnotebook.app.utils.rememberMicPermissionLauncher
import songwritersnotebook.app.tutorial.TutorialManager
import songwritersnotebook.app.tutorial.TutorialStep
import songwritersnotebook.app.tutorial.tutorialTarget
import songwritersnotebook.app.tutorial.TutorialOverlay
import kotlinx.coroutines.launch
import kotlin.math.roundToInt

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AiAssistantPanel(
    isVisible: Boolean,
    onDismiss: () -> Unit,
    viewModel: EditorViewModel,
    userProfile: UserProfile?,
    bottomPadding: PaddingValues = PaddingValues(0.dp)
) {
    val uiState by viewModel.uiState.collectAsState()
    val scope = rememberCoroutineScope()
    val currentStep by TutorialManager.currentStep.collectAsState()
    
    // 1. Height Management
    val density = LocalDensity.current
    val fullHeightRatio = 0.98f
    val halfHeightRatio = 0.65f
    
    // Harmony Input State
    var harmonyText by remember { mutableStateOf("") }
    
    val isWeb = remember { songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true) }
    
    // Web Drag & Minimize State
    var offsetX by remember { mutableStateOf(0f) }
    var offsetY by remember { mutableStateOf(0f) }
    var savedOffsetX by remember { mutableStateOf(0f) }
    var savedOffsetY by remember { mutableStateOf(0f) }
    var isMinimized by remember { mutableStateOf(false) }

    if (!isVisible) {
        // Reset state on close
        if (isWeb) {
            offsetX = 0f
            offsetY = 0f
            savedOffsetX = 0f
            savedOffsetY = 0f
            isMinimized = false
        }
        return
    }

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .padding(bottomPadding) // Anchor to top of EditorBottomBar
    ) {
        val maxHeightPx = constraints.maxHeight.toFloat()
        val fullHeightPx = maxHeightPx * fullHeightRatio
        val halfHeightPx = maxHeightPx * halfHeightRatio
        
        // Use Animatable for smooth height transitions
        val panelHeight = remember { Animatable(halfHeightPx) }
        
        LaunchedEffect(isVisible) {
            if (isVisible && panelHeight.value < halfHeightPx) {
                panelHeight.snapTo(0f)
                panelHeight.animateTo(halfHeightPx)
            }
        }

        // 2. Transparent dismissal area (Top portion of screen)
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(with(density) { (maxHeightPx - panelHeight.value).toDp() })
                .align(Alignment.TopCenter)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onDismiss
                )
        )

        // 3. The Sliding Panel Container
        val panelModifier = if (isWeb) {
            Modifier
                .align(Alignment.BottomEnd)
                .offset { IntOffset(offsetX.roundToInt(), offsetY.roundToInt()) }
                .padding(end = 16.dp, bottom = 16.dp)
                .widthIn(max = 400.dp)
                .then(
                    if (isMinimized) Modifier.wrapContentHeight()
                    else Modifier.height(with(density) { panelHeight.value.toDp() })
                )
        } else {
            Modifier
                .align(Alignment.BottomCenter)
                .fillMaxWidth()
                .adaptiveSheetWidth()
                .height(with(density) { panelHeight.value.toDp() })
                .draggable(
                    orientation = Orientation.Vertical,
                    state = rememberDraggableState { delta ->
                        scope.launch {
                            val newHeight = (panelHeight.value - delta).coerceIn(0f, fullHeightPx)
                            panelHeight.snapTo(newHeight)
                        }
                    },
                    onDragStopped = { velocity ->
                        val targetHeight = when {
                            velocity > 500 -> 0f // Swipe down to close
                            panelHeight.value < maxHeightPx * 0.25f -> 0f
                            panelHeight.value < maxHeightPx * 0.7f -> halfHeightPx
                            else -> fullHeightPx
                        }
                        
                        scope.launch {
                            panelHeight.animateTo(targetHeight, spring(stiffness = Spring.StiffnessLow))
                            if (targetHeight == 0f) onDismiss()
                        }
                    }
                )
        }

        Surface(
            modifier = panelModifier,
            shape = if (isWeb) RoundedCornerShape(16.dp) else RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp),
            color = MaterialTheme.colorScheme.surface,
            tonalElevation = 8.dp,
            shadowElevation = 16.dp
        ) {
            Column(modifier = if (isWeb && isMinimized) Modifier.fillMaxWidth() else Modifier.fillMaxSize()) {
                // DRAG HANDLE OR CLOSE BUTTON
                if (isWeb) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(MaterialTheme.colorScheme.surfaceColorAtElevation(3.dp), RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp))
                            .pointerInput(Unit) {
                                detectDragGestures { change, dragAmount ->
                                    change.consume()
                                    offsetX += dragAmount.x
                                    offsetY += dragAmount.y
                                }
                            }
                            .padding(top = 12.dp, bottom = 12.dp, start = 16.dp, end = 12.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Icon(Icons.Default.AutoAwesome, contentDescription = null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(18.dp))
                            Text("Harmony Assistant", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                        }
                        Row(horizontalArrangement = Arrangement.End) {
                            IconButton(onClick = { 
                                if (isMinimized) {
                                    // Restore previous position
                                    offsetX = savedOffsetX
                                    offsetY = savedOffsetY
                                    isMinimized = false
                                } else {
                                    // Save current position and snap to bottom right (0,0)
                                    savedOffsetX = offsetX
                                    savedOffsetY = offsetY
                                    offsetX = 0f
                                    offsetY = 0f
                                    isMinimized = true
                                }
                            }, modifier = Modifier.size(24.dp)) {
                                Icon(if (isMinimized) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown, contentDescription = "Minimize", modifier = Modifier.size(20.dp))
                            }
                            Spacer(Modifier.width(8.dp))
                            IconButton(onClick = {
                                offsetX = 0f
                                offsetY = 0f
                                savedOffsetX = 0f
                                savedOffsetY = 0f
                                isMinimized = false
                                onDismiss()
                            }, modifier = Modifier.size(24.dp)) {
                                Icon(Icons.Default.Close, contentDescription = "Close", modifier = Modifier.size(20.dp))
                            }
                        }
                    }
                } else {
                    Box(modifier = Modifier.fillMaxWidth().padding(top = 12.dp, bottom = 8.dp), contentAlignment = Alignment.Center) {
                        Box(modifier = Modifier.size(32.dp, 4.dp).background(MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f), CircleShape))
                    }
                }

                if (!isWeb || !isMinimized) {
                    // HEADER
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        val isStudio = userProfile?.subscriptionTier == "STUDIO"
                        val isAdmin = userProfile?.isAdmin == true
                        val totalCredits = userProfile?.totalAiCredits ?: 0

                        if (!isStudio) {
                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                Text("Credits: $totalCredits", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                            }
                        }
                        
                        val showAdButton = !isWeb && (!isStudio || totalCredits <= 0)
                        
                        if (showAdButton) {
                            val context = LocalPlatformContext.current
                            AdRewardButton(
                                userProfile = userProfile,
                                onClick = { if (context != null) viewModel.requestRewardedAiUses(context) }
                            )
                        }
                    }

                    // SCROLLABLE CONTENT AREA
                    Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                        HarmonyAiTabContent(viewModel)
                    }

                    // 4. PINNED BOTTOM ACTION BAR (Always visible at the bottom of the container)
                    val barColor = MaterialTheme.colorScheme.surfaceColorAtElevation(3.dp)
                    Surface(
                        color = barColor,
                        shadowElevation = 8.dp,
                        modifier = Modifier.fillMaxWidth().imePadding()
                    ) {
                        Box(modifier = Modifier.padding(16.dp)) {
                            val totalCredits = userProfile?.totalAiCredits ?: 0
                            
                            HarmonyInputBar(
                                harmonyText, 
                                { harmonyText = it }, 
                                viewModel, 
                                uiState,
                                userProfile,
                                modifier = Modifier.tutorialTarget(
                                    TutorialStep.EDITOR_AI_HARMONY,
                                    isSecondary = true
                                ),
                                onFocus = {
                                    val isWebCheck = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
                                    if (!isWebCheck) {
                                        scope.launch {
                                            panelHeight.animateTo(fullHeightPx, spring(stiffness = Spring.StiffnessLow))
                                        }
                                    }
                                }
                            )
                        }
                    }
                }
            }
        }

        TutorialOverlay(isGlobal = false)
    }
}



@Composable
fun HarmonyInputBar(
    textInput: String,
    onValueChange: (String) -> Unit,
    viewModel: EditorViewModel,
    uiState: EditorUiState,
    userProfile: UserProfile?,
    modifier: Modifier = Modifier,
    onFocus: () -> Unit = {}
) {
    val micPermissionLauncher = rememberMicPermissionLauncher(
        onGranted = { viewModel.onAiAssistantToggleListening() },
        onDenied = { }
    )

    val isListening = uiState.isHarmonyListening
    val hasText = textInput.isNotBlank()

    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = modifier.fillMaxWidth()
    ) {
        OutlinedTextField(
            value = textInput,
            onValueChange = onValueChange,
            modifier = Modifier.weight(1f).height(56.dp)
                .onFocusChanged { if (it.isFocused) onFocus() }
                .onPreviewKeyEvent { event ->
                    if (event.key == Key.Enter && event.type == KeyEventType.KeyUp) {
                        if (hasText) {
                            viewModel.sendHarmonyTextMessage(textInput)
                            onValueChange("")
                        }
                        true
                    } else false
                },
            placeholder = { Text("Ask Harmony...", style = MaterialTheme.typography.bodySmall) },
            shape = CircleShape,
            singleLine = true,
            keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(imeAction = androidx.compose.ui.text.input.ImeAction.Send),
            keyboardActions = androidx.compose.foundation.text.KeyboardActions(
                onSend = {
                    if (hasText) {
                        viewModel.sendHarmonyTextMessage(textInput)
                        onValueChange("")
                    }
                }
            ),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = MaterialTheme.colorScheme.primary,
                unfocusedBorderColor = MaterialTheme.colorScheme.outline
            ),
            enabled = !uiState.isHarmonyThinking && !uiState.isHarmonyListening
        )
        
        val buttonColor = if (isListening) MaterialTheme.colorScheme.errorContainer else if (hasText) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.primaryContainer
        val iconColor = if (isListening) MaterialTheme.colorScheme.onErrorContainer else if (hasText) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onPrimaryContainer
        
        val totalCredits = userProfile?.totalAiCredits ?: 0

        Surface(
            shape = CircleShape,
            color = buttonColor,
            modifier = Modifier.size(40.dp).clickable(enabled = !uiState.isHarmonyThinking) {
                if (totalCredits <= 0 && !isListening) {
                    // Trigger purchase dialog
                    viewModel.triggerAiLimitDialog()
                    return@clickable
                }

                if (hasText) {
                    viewModel.sendHarmonyTextMessage(textInput)
                    onValueChange("")
                } else {
                    micPermissionLauncher()
                }
            }
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    imageVector = if (hasText) Icons.AutoMirrored.Filled.Send else Icons.Default.Mic,
                    contentDescription = null,
                    modifier = Modifier.size(20.dp),
                    tint = iconColor
                )
            }
        }
    }
}
